import type {
  GetCOVOrderDetailParams,
  GetCOVOrdersParams,
  GetOrderParams,
  GetPromotionsParams,
  IMarketplaceOrderDetail,
  IOrderService,
  OrderService,
  VatInfo,
  VatOrderInfo,
} from '@teko/order';
import {
  GetOrdersV2Params,
  GetOrdersV2Response,
  IOrderDetailV2,
} from '@teko/order/v2';

class OrderServiceWrapper implements IOrderService {
  private baseUrl: string;
  private servicePromise: Promise<OrderService> | undefined;
  private language: string | undefined;

  constructor(baseUrl: string, language?: string) {
    this.baseUrl = baseUrl;
    this.language = language;
  }

  private async initializeService(): Promise<OrderService> {
    if (!this.servicePromise) {
      this.servicePromise = import('@teko/order').then(
        (mod) => new mod.OrderService(this.baseUrl, this.language),
      );
    }
    return this.servicePromise;
  }

  async getOrder(params: GetOrderParams): Promise<IMarketplaceOrderDetail> {
    const service = await this.initializeService();
    return service.getOrder(params);
  }

  async getOrders(params: GetCOVOrdersParams) {
    const service = await this.initializeService();
    return service.getOrders(params);
  }

  async getOrderDetail(params: GetCOVOrderDetailParams) {
    const service = await this.initializeService();
    return service.getOrderDetail(params);
  }

  async importOrder(orderId: string) {
    const service = await this.initializeService();
    return service.importOrder(orderId);
  }

  async updateVatInfo(orderId: string, vatInfo: VatInfo) {
    const service = await this.initializeService();
    return service.updateVatInfo(orderId, vatInfo);
  }

  async updateOrderVatInfo(orderId: string, vatInfo: VatOrderInfo) {
    const service = await this.initializeService();
    return service.updateOrderVatInfo(orderId, vatInfo);
  }

  async getPromotions(params: GetPromotionsParams) {
    const service = await this.initializeService();
    return service.getPromotions(params);
  }

  async getOrderDetailV2(
    orderId: string,
    platformId: number,
  ): Promise<IOrderDetailV2> {
    const service = await this.initializeService();
    return service.getOrderDetailV2(orderId, platformId);
  }

  async getOrdersV2(params: GetOrdersV2Params): Promise<GetOrdersV2Response> {
    const service = await this.initializeService();
    return service.getOrdersV2(params);
  }
}

export default OrderServiceWrapper;
